import { doc, getDoc, getDocs, limit, orderBy, query, startAfter, Timestamp, where } from "firebase/firestore";
import { tasksCollection } from "../models/taskModel";
import { db } from "../../firebase/firebase";
import moment from "moment-timezone";

export const getAllTasks = async ({
  limitCount = 10,
  startAfterDoc = null,
  sortBy = "lastModifiedTimestamp",
  assigneeId = "",
  startDate = "",
  endDate = "",
  status = "",
  initialInfoMedium = "",
  finalInfoMedium = "",
  active = "",
}) => {
  let queryConstraints = [];

  if (assigneeId) {
    const assigneeRef = doc(db, "users", assigneeId);
    queryConstraints.push(where("responsiblePerson", "==", assigneeRef));
  }

  if (status) {
    queryConstraints.push(where("taskStatus", "==", status));
  }

  if (initialInfoMedium) {
    queryConstraints.push(where("initialTaskInformationMedium", "==", initialInfoMedium));
  }

  if (finalInfoMedium) {
    queryConstraints.push(where("finalTaskInformationMedium", "==", finalInfoMedium));
  }

  if (active !== "") {
    queryConstraints.push(where("active", "==", active));
  }

  let orderByField = sortBy;

  if (startDate && endDate) {
    const startTimestamp = Timestamp.fromDate(
      moment(startDate, "MM-DD-YYYY").utcOffset("+05:30").startOf("day").toDate()
    );
    const endTimestamp = Timestamp.fromDate(
      moment(endDate, "MM-DD-YYYY").utcOffset("+05:30").endOf("day").toDate()
    );
    queryConstraints.push(where("taskDate", ">=", startTimestamp), where("taskDate", "<=", endTimestamp));
    orderByField = "taskDate";
  }

  queryConstraints.push(orderBy(orderByField, "desc"));
  queryConstraints.push(limit(limitCount));

  if (startAfterDoc) {
    const startRef = doc(db, "tasks", startAfterDoc);
    const startSnap = await getDoc(startRef);
    queryConstraints.push(startAfter(startSnap));
  }

  const baseQuery = query(tasksCollection, ...queryConstraints);
  const snapshot = await getDocs(baseQuery);

  const tasks = snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  }));

  const formattedTasks = await Promise.all(
    tasks.map(async (task) => {
      const {
        title,
        taskStatus,
        taskDate,
        remainder,
        responsiblePerson,
        createdBy,
        createdTimestamp,
        lastModifiedTimestamp,
        taskInitiatedByBoolean,
        initialTaskInformationMedium,
        finalTaskInformationMedium,
      } = task;

      const [assigneeSnap, createdBySnap] = await Promise.all([
        responsiblePerson ? getDoc(responsiblePerson) : Promise.resolve(null),
        createdBy ? getDoc(createdBy) : Promise.resolve(null),
      ]);

      const assigneeData = assigneeSnap?.exists() ? assigneeSnap.data() : null;
      const assignee = assigneeData
        ? { name: assigneeData.name || "Unknown", id: assigneeSnap.id }
        : { name: "Unknown", id: "" };

      const createdByData = createdBySnap?.exists() ? createdBySnap.data() : null;

      const taskDateJs = taskDate?.toDate ? taskDate.toDate() : null;
      const createdDateJs = createdTimestamp?.toDate ? createdTimestamp.toDate() : null;
      const lastModifiedJs = lastModifiedTimestamp?.toDate ? lastModifiedTimestamp.toDate() : null;

      return {
        id: task.id,
        title,
        assignee,
        taskDate: taskDateJs ? moment(taskDateJs).tz("Asia/Kolkata").format("DD-MM-YYYY hh:mm A") : "-",
        rawTaskDate: taskDateJs ? moment(taskDateJs).tz("Asia/Kolkata").format("YYYY-MM-DD") : "",
        createdDate: createdDateJs ? moment(createdDateJs).tz("Asia/Kolkata").format("DD-MM-YYYY hh:mm A") : "-",
        lastModified: lastModifiedJs ? moment(lastModifiedJs).tz("Asia/Kolkata").format("DD-MM-YYYY hh:mm A") : "-",
        remainder,
        status: taskStatus,
        createdBy: createdByData?.name || "Unknown",
        taskInitiatedByBoolean,
        initialTaskInformationMedium,
        finalTaskInformationMedium,
      };
    })
  );

  return {
    tasks: formattedTasks,
    firstVisible: snapshot.docs[0],
    lastVisible: snapshot.docs[snapshot.docs.length - 1] || null,
  };
};
